import React, { useEffect, useState } from 'react';
import { X, Plus, Trash2, Sparkles, ChevronDown } from 'lucide-react';
import { Input } from '../SubscriptionAppInput';
import { Button } from '../SubscriptionAppButton';
interface AutomationRule {
  id: string;
  event: string;
  eventValue?: string | number;
  condition?: {
    field: string;
    operator: string;
    value: string | number;
  };
  action: 'apply' | 'remove';
}
interface EditTagModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (tagData: {
    label: string;
    color: string;
    rules: AutomationRule[];
  }) => void;
  onDelete?: () => void;
  tag: {
    id: string;
    label: string;
    color: string;
    rules?: AutomationRule[];
  };
}
const COLOR_PRESETS = [{
  name: 'Sky Blue',
  value: '#0EA5E9'
}, {
  name: 'Green',
  value: '#10B981'
}, {
  name: 'Red',
  value: '#EF4444'
}, {
  name: 'Amber',
  value: '#F59E0B'
}, {
  name: 'Purple',
  value: '#8B5CF6'
}, {
  name: 'Pink',
  value: '#EC4899'
}, {
  name: 'Indigo',
  value: '#6366F1'
}, {
  name: 'Gray',
  value: '#64748B'
}, {
  name: 'Teal',
  value: '#14B8A6'
}, {
  name: 'Orange',
  value: '#F97316'
}];
const EVENT_OPTIONS = [{
  group: 'Subscription Events',
  options: [{
    value: 'subscription_created',
    label: 'Subscription created'
  }, {
    value: 'subscription_cancelled',
    label: 'Subscription cancelled'
  }, {
    value: 'subscription_paused',
    label: 'Subscription paused'
  }, {
    value: 'subscription_resumed',
    label: 'Subscription resumed'
  }, {
    value: 'subscription_on_hold',
    label: 'Subscription on hold'
  }, {
    value: 'orders_completed',
    label: 'Orders completed =',
    needsValue: true,
    valueType: 'number'
  }, {
    value: 'member_for_months',
    label: 'Member for',
    needsValue: true,
    valueType: 'number',
    valueSuffix: 'months'
  }]
}, {
  group: 'Payment Events',
  options: [{
    value: 'payment_failed',
    label: 'Payment failed'
  }, {
    value: 'payment_recovered',
    label: 'Payment recovered'
  }, {
    value: 'payment_method_updated',
    label: 'Payment method updated'
  }, {
    value: 'payment_method_expired',
    label: 'Payment method expired'
  }]
}, {
  group: 'Engagement Events',
  options: [{
    value: 'no_activity_days',
    label: 'No activity for',
    needsValue: true,
    valueType: 'number',
    valueSuffix: 'days'
  }, {
    value: 'orders_skipped',
    label: 'Orders skipped =',
    needsValue: true,
    valueType: 'number'
  }]
}, {
  group: 'Value Events',
  options: [{
    value: 'ltv_reaches',
    label: 'LTV reaches',
    needsValue: true,
    valueType: 'currency'
  }, {
    value: 'aov_above',
    label: 'AOV above',
    needsValue: true,
    valueType: 'currency'
  }]
}];
const CONDITION_FIELDS = [{
  value: 'retry_count',
  label: 'Retry count'
}, {
  value: 'subscription_status',
  label: 'Subscription status'
}, {
  value: 'days_since_last_order',
  label: 'Days since last order'
}, {
  value: 'total_orders',
  label: 'Total orders'
}];
const CONDITION_OPERATORS = [{
  value: 'equals',
  label: 'equals'
}, {
  value: 'greater_than',
  label: 'greater than'
}, {
  value: 'less_than',
  label: 'less than'
}, {
  value: 'gte',
  label: '≥'
}, {
  value: 'lte',
  label: '≤'
}];
export function EditTagModal({
  isOpen,
  onClose,
  onSave,
  onDelete,
  tag
}: EditTagModalProps) {
  const [tagLabel, setTagLabel] = useState(tag.label);
  const [tagColor, setTagColor] = useState(tag.color);
  const [rules, setRules] = useState<AutomationRule[]>(tag.rules || []);
  const [showExamples, setShowExamples] = useState(false);
  useEffect(() => {
    if (isOpen) {
      setTagLabel(tag.label);
      setTagColor(tag.color);
      setRules(tag.rules || []);
    }
  }, [isOpen, tag]);
  if (!isOpen) return null;
  const handleAddRule = () => {
    const newRule: AutomationRule = {
      id: `rule-${Date.now()}`,
      event: '',
      action: 'apply'
    };
    setRules([...rules, newRule]);
  };
  const handleDeleteRule = (ruleId: string) => {
    setRules(rules.filter(r => r.id !== ruleId));
  };
  const handleUpdateRule = (ruleId: string, updates: Partial<AutomationRule>) => {
    setRules(rules.map(r => r.id === ruleId ? {
      ...r,
      ...updates
    } : r));
  };
  const handleAddCondition = (ruleId: string) => {
    handleUpdateRule(ruleId, {
      condition: {
        field: '',
        operator: '',
        value: ''
      }
    });
  };
  const handleRemoveCondition = (ruleId: string) => {
    handleUpdateRule(ruleId, {
      condition: undefined
    });
  };
  const handleSave = () => {
    // Validate
    if (!tagLabel.trim()) {
      alert('Tag name is required');
      return;
    }
    // Validate rules
    for (const rule of rules) {
      if (!rule.event) {
        alert('Each rule must have an event selected');
        return;
      }
      const eventOption = EVENT_OPTIONS.flatMap(g => g.options).find(o => o.value === rule.event);
      if (eventOption?.needsValue && !rule.eventValue) {
        alert(`Please provide a value for "${eventOption.label}"`);
        return;
      }
    }
    onSave({
      label: tagLabel,
      color: tagColor,
      rules
    });
    onClose();
  };
  const getEventOption = (eventValue: string) => {
    return EVENT_OPTIONS.flatMap(g => g.options).find(o => o.value === eventValue);
  };
  return <div className="fixed inset-0 z-[1050] flex items-center justify-center p-4" data-id="element-1391">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-[#0F172A] bg-opacity-40" onClick={onClose} data-id="element-1392" />

      {/* Modal */}
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-[600px] max-h-[85vh] flex flex-col overflow-hidden" data-id="element-1393">
        {/* Header */}
        <div className="flex-shrink-0 p-6 border-b border-[#E2E8F0]" data-id="element-1394">
          <div className="flex items-start justify-between" data-id="element-1395">
            <div data-id="element-1396">
              <h2 className="text-[20px] font-semibold text-[#0F172A]" data-id="element-1397">
                Edit Tag
              </h2>
              <p className="text-[14px] text-[#64748B] mt-1" data-id="element-1398">
                Configure tag details and automation rules
              </p>
            </div>
            <button onClick={onClose} className="p-1 hover:bg-[#F1F5F9] rounded transition-colors" data-id="element-1399">
              <X className="w-5 h-5 text-[#64748B]" data-id="element-1400" />
            </button>
          </div>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6" data-id="element-1401">
          {/* Section 1: Tag Details */}
          <div data-id="element-1402">
            <h3 className="text-[14px] font-semibold text-[#0F172A] mb-4" data-id="element-1403">
              Tag Details
            </h3>

            {/* Tag Name */}
            <div className="mb-4" data-id="element-1404">
              <label className="block text-[14px] font-medium text-[#374151] mb-2" data-id="element-1405">
                Tag Name
              </label>
              <Input type="text" value={tagLabel} onChange={e => setTagLabel(e.target.value)} placeholder="Enter tag name" data-id="element-1406" />
            </div>

            {/* Tag Color */}
            <div data-id="element-1407">
              <label className="block text-[14px] font-medium text-[#374151] mb-2" data-id="element-1408">
                Color
              </label>
              <div className="flex flex-wrap gap-2" data-id="element-1409">
                {COLOR_PRESETS.map(color => <button key={color.value} onClick={() => setTagColor(color.value)} className={`w-8 h-8 rounded-full transition-all ${tagColor === color.value ? 'ring-2 ring-[#0EA5E9] ring-offset-2' : 'hover:scale-110'}`} style={{
                backgroundColor: color.value
              }} title={color.name} data-id="element-1410" />)}
              </div>
            </div>
          </div>

          {/* Section 2: Automation Rules */}
          <div data-id="element-1411">
            <div className="mb-4" data-id="element-1412">
              <h3 className="text-[14px] font-semibold text-[#0F172A]" data-id="element-1413">
                Automation Rules
              </h3>
              <p className="text-[13px] text-[#64748B] mt-1" data-id="element-1414">
                Automatically apply or remove this tag based on events
              </p>
            </div>

            {/* Rules List */}
            {rules.length === 0 ? <div className="border-2 border-dashed border-[#E2E8F0] rounded-lg p-8 text-center" data-id="element-1415">
                <Sparkles className="w-8 h-8 text-[#94A3B8] mx-auto mb-3" data-id="element-1416" />
                <p className="text-[14px] font-medium text-[#0F172A] mb-1" data-id="element-1417">
                  No automation rules yet
                </p>
                <p className="text-[13px] text-[#64748B] mb-4" data-id="element-1418">
                  Add rules to automatically apply or remove this tag
                </p>
                <Button variant="outline" onClick={handleAddRule} iconLeft={<Plus className="w-4 h-4" data-id="element-1420" />} data-id="element-1419">
                  Add Rule
                </Button>
              </div> : <div className="space-y-2" data-id="element-1421">
                {rules.map(rule => {
              const eventOption = getEventOption(rule.event);
              return <div key={rule.id} className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg p-4 space-y-3" data-id="element-1422">
                      {/* Delete Rule Button */}
                      <div className="flex justify-end" data-id="element-1423">
                        <button onClick={() => handleDeleteRule(rule.id)} className="p-1 text-[#64748B] hover:text-[#EF4444] hover:bg-[#FEE2E2] rounded transition-colors" title="Delete rule" data-id="element-1424">
                          <Trash2 className="w-4 h-4" data-id="element-1425" />
                        </button>
                      </div>

                      {/* Event Selection */}
                      <div data-id="element-1426">
                        <label className="block text-[12px] text-[#64748B] mb-2" data-id="element-1427">
                          When
                        </label>
                        <div className="flex gap-2 items-center" data-id="element-1428">
                          <select value={rule.event} onChange={e => handleUpdateRule(rule.id, {
                      event: e.target.value,
                      eventValue: undefined
                    })} className="flex-1 h-[40px] pl-3 pr-10 border border-[#CBD5E1] rounded-md bg-white text-[14px] text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:border-transparent appearance-none bg-no-repeat bg-right" style={{
                      backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%23475569' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`,
                      backgroundPosition: 'right 12px center',
                      backgroundSize: '16px 16px'
                    }} data-id="element-1429">
                            <option value="" data-id="element-1430">Select event...</option>
                            {EVENT_OPTIONS.map(group => <optgroup key={group.group} label={group.group} data-id="element-1431">
                                {group.options.map(option => <option key={option.value} value={option.value} data-id="element-1432">
                                    {option.label}
                                  </option>)}
                              </optgroup>)}
                          </select>

                          {/* Dynamic Value Input */}
                          {eventOption?.needsValue && <>
                              <input type={eventOption.valueType === 'currency' ? 'number' : 'number'} value={rule.eventValue || ''} onChange={e => handleUpdateRule(rule.id, {
                        eventValue: e.target.value
                      })} placeholder={eventOption.valueType === 'currency' ? '500' : '3'} className="w-20 h-[40px] px-3 border border-[#CBD5E1] rounded-md bg-white text-[14px] text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:border-transparent" data-id="element-1433" />
                              {eventOption.valueSuffix && <span className="text-[14px] text-[#64748B]" data-id="element-1434">
                                  {eventOption.valueSuffix}
                                </span>}
                            </>}
                        </div>
                      </div>

                      {/* Condition (Optional) */}
                      {!rule.condition ? <button onClick={() => handleAddCondition(rule.id)} className="text-[13px] text-[#0EA5E9] hover:underline flex items-center gap-1" data-id="element-1435">
                          <Plus className="w-3 h-3" data-id="element-1436" />
                          Add Condition
                        </button> : <div className="bg-white border border-[#E2E8F0] rounded-md p-3 space-y-2" data-id="element-1437">
                          <div className="flex items-center justify-between mb-2" data-id="element-1438">
                            <label className="text-[12px] text-[#64748B]" data-id="element-1439">
                              Condition
                            </label>
                            <button onClick={() => handleRemoveCondition(rule.id)} className="text-[#64748B] hover:text-[#EF4444]" data-id="element-1440">
                              <X className="w-4 h-4" data-id="element-1441" />
                            </button>
                          </div>
                          <div className="grid grid-cols-3 gap-2" data-id="element-1442">
                            <select value={rule.condition.field} onChange={e => handleUpdateRule(rule.id, {
                      condition: {
                        ...rule.condition!,
                        field: e.target.value
                      }
                    })} className="h-[36px] pl-2 pr-8 border border-[#CBD5E1] rounded-md bg-white text-[13px] text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] appearance-none bg-no-repeat bg-right" style={{
                      backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='%23475569' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`,
                      backgroundPosition: 'right 8px center',
                      backgroundSize: '14px 14px'
                    }} data-id="element-1443">
                              <option value="" data-id="element-1444">Field...</option>
                              {CONDITION_FIELDS.map(field => <option key={field.value} value={field.value} data-id="element-1445">
                                  {field.label}
                                </option>)}
                            </select>
                            <select value={rule.condition.operator} onChange={e => handleUpdateRule(rule.id, {
                      condition: {
                        ...rule.condition!,
                        operator: e.target.value
                      }
                    })} className="h-[36px] pl-2 pr-8 border border-[#CBD5E1] rounded-md bg-white text-[13px] text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] appearance-none bg-no-repeat bg-right" style={{
                      backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='%23475569' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`,
                      backgroundPosition: 'right 8px center',
                      backgroundSize: '14px 14px'
                    }} data-id="element-1446">
                              <option value="" data-id="element-1447">Operator...</option>
                              {CONDITION_OPERATORS.map(op => <option key={op.value} value={op.value} data-id="element-1448">
                                  {op.label}
                                </option>)}
                            </select>
                            <input type="text" value={rule.condition.value} onChange={e => handleUpdateRule(rule.id, {
                      condition: {
                        ...rule.condition!,
                        value: e.target.value
                      }
                    })} placeholder="Value" className="h-[36px] px-2 border border-[#CBD5E1] rounded-md bg-white text-[13px] text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#0EA5E9]" data-id="element-1449" />
                          </div>
                        </div>}

                      {/* Action Selection */}
                      <div data-id="element-1450">
                        <label className="block text-[12px] text-[#64748B] mb-2" data-id="element-1451">
                          Action
                        </label>
                        <div className="flex gap-2" data-id="element-1452">
                          <button onClick={() => handleUpdateRule(rule.id, {
                      action: 'apply'
                    })} className={`flex-1 h-[36px] px-4 rounded-md text-[14px] font-medium transition-all ${rule.action === 'apply' ? 'bg-[#0EA5E9] text-white' : 'bg-white text-[#64748B] border border-[#E2E8F0] hover:border-[#CBD5E1]'}`} data-id="element-1453">
                            Apply Tag
                          </button>
                          <button onClick={() => handleUpdateRule(rule.id, {
                      action: 'remove'
                    })} className={`flex-1 h-[36px] px-4 rounded-md text-[14px] font-medium transition-all ${rule.action === 'remove' ? 'bg-[#64748B] text-white' : 'bg-white text-[#64748B] border border-[#E2E8F0] hover:border-[#CBD5E1]'}`} data-id="element-1454">
                            Remove Tag
                          </button>
                        </div>
                      </div>
                    </div>;
            })}

                {/* Add Rule Button */}
                <button onClick={handleAddRule} className="w-full h-[40px] border-2 border-dashed border-[#CBD5E1] rounded-lg text-[14px] text-[#0EA5E9] hover:border-[#0EA5E9] hover:bg-[#F0F9FF] transition-all flex items-center justify-center gap-2" data-id="element-1455">
                  <Plus className="w-4 h-4" data-id="element-1456" />
                  Add Rule
                </button>
              </div>}

            {/* Examples */}
            <div className="mt-4" data-id="element-1457">
              <button onClick={() => setShowExamples(!showExamples)} className="text-[13px] text-[#0EA5E9] hover:underline flex items-center gap-1" data-id="element-1458">
                {showExamples ? 'Hide examples' : 'See examples'}
                <ChevronDown className={`w-4 h-4 transition-transform ${showExamples ? 'rotate-180' : ''}`} data-id="element-1459" />
              </button>
              {showExamples && <div className="mt-3 p-3 bg-[#F8FAFC] border border-[#E2E8F0] rounded-md space-y-2" data-id="element-1460">
                  <p className="text-[13px] text-[#64748B]" data-id="element-1461">
                    • When Payment failed + Retry count &gt; 2 → Apply tag
                  </p>
                  <p className="text-[13px] text-[#64748B]" data-id="element-1462">
                    • When Payment recovered → Remove tag
                  </p>
                  <p className="text-[13px] text-[#64748B]" data-id="element-1463">
                    • When Orders completed = 3 → Apply tag
                  </p>
                </div>}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex-shrink-0 border-t border-[#E2E8F0] bg-[#F8FAFC] p-4" data-id="element-1464">
          <div className="flex items-center justify-between" data-id="element-1465">
            {onDelete && <button onClick={onDelete} className="text-[14px] text-[#EF4444] hover:underline" data-id="element-1466">
                Delete Tag
              </button>}
            <div className={`flex gap-3 ${!onDelete ? 'ml-auto' : ''}`} data-id="element-1467">
              <Button variant="outline" onClick={onClose} data-id="element-1468">
                Cancel
              </Button>
              <Button variant="primary" onClick={handleSave} data-id="element-1469">
                Save Changes
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>;
}